/**
 * Structured data (§45). Tells search engines what a page is about in the
 * vocabulary they read, so listings can show prices, breadcrumbs and business
 * details rather than a bare link.
 *
 * Every builder returns plain JSON-LD. Nothing is asserted that the page does
 * not already show a visitor: no invented ratings, no made-up opening hours.
 */
import { company } from '@/config/company';
import { env } from '@/config/env';
import { type Accessory } from '@/services/accessories/types';
import { type VehicleListing } from '@/services/vehicles/types';
import { vehiclePath, vehicleTitle } from '@/utils/vehicleFormat';

type Json = Record<string, unknown>;

const absolute = (path: string): string | null => (env.siteUrl ? `${env.siteUrl}${path}` : null);

const postalAddress = (): Json => ({
  '@type': 'PostalAddress',
  streetAddress: `${company.address.building}, ${company.address.street}`,
  addressLocality: company.address.city,
  addressCountry: company.address.countryCode,
  postOfficeBoxNumber: company.address.poBox,
});

/**
 * The business itself. AutoDealer is the closest type to what Kabex does, and
 * it is what lets a search engine show the address and phone number.
 */
export function organizationSchema(): Json {
  const url = absolute('/');
  return {
    '@context': 'https://schema.org',
    '@type': 'AutoDealer',
    name: company.brandName,
    legalName: company.legalName,
    slogan: company.motto,
    foundingDate: String(company.established),
    email: [company.inquiryEmail, company.email],
    telephone: company.phones.map((p) => p.e164),
    address: postalAddress(),
    openingHoursSpecification: company.openingHours.map((h) => ({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: h.days,
      opens: h.opens,
      closes: h.closes,
    })),
    areaServed: ['Uganda', 'DR Congo', 'South Sudan'],
    ...(url ? { url } : {}),
  };
}

/** Lets a search engine offer a site-wide search box for the car listings. */
export function websiteSchema(): Json {
  const url = absolute('/');
  if (!url)
    return { '@context': 'https://schema.org', '@type': 'WebSite', name: company.brandName };
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: company.brandName,
    url,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${env.siteUrl}/cars?make={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

export function breadcrumbSchema(items: { label: string; to?: string }[]): Json {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.label,
      ...(item.to && absolute(item.to) ? { item: absolute(item.to) } : {}),
    })),
  };
}

/** Maps how Kabex describes availability onto the schema.org vocabulary. */
const availabilityUrl = (availability: VehicleListing['availability']): string => {
  if (availability === 'sold') return 'https://schema.org/SoldOut';
  if (availability === 'reserved') return 'https://schema.org/LimitedAvailability';
  return 'https://schema.org/InStock';
};

/**
 * One car. The offer is only included when there is a real price to state —
 * a listing marked "price on request" says exactly that, and inventing a
 * number for a crawler would put a false price in search results.
 */
export function vehicleSchema(vehicle: VehicleListing, imageUrl?: string | null): Json {
  const url = absolute(vehiclePath(vehicle));
  const price = vehicle.price.usd;

  return {
    '@context': 'https://schema.org',
    '@type': 'Car',
    name: vehicleTitle(vehicle),
    ...(vehicle.description ? { description: vehicle.description } : {}),
    brand: { '@type': 'Brand', name: vehicle.make },
    ...(vehicle.model ? { model: vehicle.model } : {}),
    ...(vehicle.year ? { productionDate: String(vehicle.year) } : {}),
    ...(vehicle.mileageKm !== null
      ? {
          mileageFromOdometer: {
            '@type': 'QuantitativeValue',
            value: vehicle.mileageKm,
            unitCode: 'KMT',
          },
        }
      : {}),
    ...(vehicle.engineCc
      ? {
          vehicleEngine: {
            '@type': 'EngineSpecification',
            engineDisplacement: {
              '@type': 'QuantitativeValue',
              value: vehicle.engineCc,
              unitCode: 'CMQ',
            },
          },
        }
      : {}),
    ...(vehicle.transmission ? { vehicleTransmission: vehicle.transmission } : {}),
    ...(vehicle.fuelType ? { fuelType: vehicle.fuelType } : {}),
    ...(imageUrl ? { image: imageUrl } : {}),
    ...(url ? { url } : {}),
    ...(price !== null
      ? {
          offers: {
            '@type': 'Offer',
            price,
            priceCurrency: 'USD',
            availability: availabilityUrl(vehicle.availability),
            ...(url ? { url } : {}),
            seller: { '@type': 'AutoDealer', name: company.brandName },
          },
        }
      : {}),
  };
}

/**
 * One accessory.
 *
 * The offer carries a price only when the part HAS one. That used to be gated
 * on the catalogue being sample data as well, because an `Offer` is what puts a
 * figure in a search result and a figure in a search result is a representation
 * to someone who never visited the page — the page can qualify a price with the
 * word "indicative" and a rich result cannot. Since 2026-10-26 the figures are
 * Kabex's own, published deliberately from Kabex Hub, so the offer is no longer
 * suppressed; a quote-only part still carries none, which is the same rule
 * `AccessoryPrice` follows on screen.
 */
export function accessorySchema(accessory: Accessory): Json {
  const url = absolute(`/accessories/${accessory.slug}`);
  const image = accessory.cover?.url ?? null;

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: accessory.name,
    description: accessory.summary,
    // The shelf's NAME: a slug is an internal key, and `Product.category` is
    // read by people and by search engines, not by this site.
    category: accessory.categoryLabel,
    brand: { '@type': 'Brand', name: accessory.brand },
    ...(image ? { image } : {}),
    ...(url ? { url } : {}),
    ...(accessory.priceUgx !== null
      ? {
          offers: {
            '@type': 'Offer',
            price: accessory.priceUgx,
            priceCurrency: 'UGX',
            availability:
              accessory.stock === 'in-stock'
                ? 'https://schema.org/InStock'
                : 'https://schema.org/PreOrder',
            ...(url ? { url } : {}),
            seller: { '@type': 'AutoDealer', name: company.brandName },
          },
        }
      : {}),
  };
}

export function articleSchema(article: { title: string; summary: string; slug: string }): Json {
  const url = absolute(`/import-guide/${article.slug}`);
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: { '@type': 'Organization', name: company.brandName },
    publisher: { '@type': 'Organization', name: company.brandName },
    ...(url ? { url, mainEntityOfPage: url } : {}),
  };
}

/**
 * A page's questions and answers (§45). Google only shows an FAQ rich result
 * when the same questions are visible on the page, so this is built from the
 * very content the accordion renders rather than from a list of its own — the
 * two cannot drift apart.
 */
export function faqSchema(items: readonly { question: string; answer: string }[]): Json {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  };
}
